import { useForm, FormProvider, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useEffect, useCallback } from 'react';
import { Modal, ModalFooter } from '@/components/ui/modal';
import { FormInput } from '@/components/ui/form-input';
import { FormSelect } from '@/components/ui/form-select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { createCrewSchema, type CreateCrewInput } from '@/types';
import {
  CREW_PROBABILITY,
  CREW_FINANCING_TYPE,
  CREW_FINANCING_TYPE_LABELS,
  CREW_STATUS,
  CREW_STATUS_LABELS,
} from '@/types';
import { PROBABILITY_SELECT_OPTIONS } from '@/lib/crew-probability-constants';
import { useProductsSelect } from '@/hooks/useProductsSelect';
import { useVehiclesSelect } from '@/hooks/useVehiclesSelect';
import { useAssociationsSelect } from '@/hooks/useAssociationsSelect';
import { useDiscountsSelect } from '@/hooks/useDiscountsSelect';
import { useBpiLookup, type BpiContact, type BpiLookupResult } from '@/hooks/useBpiLookup';
import { AlertTriangle, CheckCircle, Loader2, Users, Search, Hash } from 'lucide-react';

/**
 * Données de création d'équipage avec les contacts BPI associés
 */
export interface CreateCrewWithBpiData {
  crewData: CreateCrewInput;
  bpiContacts: BpiContact[];
}

interface CreateCrewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateCrewWithBpiData) => Promise<void>;
}

// Options pour le select de statut
const STATUS_SELECT_OPTIONS = [
  { value: CREW_STATUS.PRE_REGISTERED, label: CREW_STATUS_LABELS[CREW_STATUS.PRE_REGISTERED] },
  { value: CREW_STATUS.BED_INCOMPLETE, label: CREW_STATUS_LABELS[CREW_STATUS.BED_INCOMPLETE] },
  { value: CREW_STATUS.BED_COMPLETE, label: CREW_STATUS_LABELS[CREW_STATUS.BED_COMPLETE] },
  {
    value: CREW_STATUS.CANCELLATION_COMPLIANT,
    label: CREW_STATUS_LABELS[CREW_STATUS.CANCELLATION_COMPLIANT],
  },
  {
    value: CREW_STATUS.CANCELLATION_NON_COMPLIANT,
    label: CREW_STATUS_LABELS[CREW_STATUS.CANCELLATION_NON_COMPLIANT],
  },
  {
    value: CREW_STATUS.POSTPONEMENT_COMPLIANT,
    label: CREW_STATUS_LABELS[CREW_STATUS.POSTPONEMENT_COMPLIANT],
  },
  {
    value: CREW_STATUS.POSTPONEMENT_NON_COMPLIANT,
    label: CREW_STATUS_LABELS[CREW_STATUS.POSTPONEMENT_NON_COMPLIANT],
  },
];

// Types de recherche BPI
type SearchMode = 'bpi_code' | 'crew_name';

export function CreateCrewModal({ open, onOpenChange, onSubmit }: CreateCrewModalProps) {
  const { products } = useProductsSelect();
  const { vehicles } = useVehiclesSelect();
  const { associations } = useAssociationsSelect();
  const { discounts } = useDiscountsSelect();

  // Hook de recherche BPI
  const {
    lookup: lookupBpi,
    lookupByCrewName,
    reset: resetBpi,
    loading: bpiLoading,
    result: bpiResult,
    multipleResults,
  } = useBpiLookup();

  // State pour la recherche
  const [searchMode, setSearchMode] = useState<SearchMode>('bpi_code');
  const [searchInput, setSearchInput] = useState('');
  const [searched, setSearched] = useState(false);
  const [selectedBpiResult, setSelectedBpiResult] = useState<BpiLookupResult | null>(null);

  const methods = useForm<CreateCrewInput>({
    resolver: zodResolver(createCrewSchema),
    defaultValues: {
      probability: CREW_PROBABILITY.MEDIUM,
      financing_type: CREW_FINANCING_TYPE.SELF_FINANCING,
      status: CREW_STATUS.PRE_REGISTERED,
      has_derogation: false,
      organizes_pvd: false,
    },
  });

  const {
    control,
    formState: { isSubmitting },
    reset,
    setValue,
  } = methods;

  // Recherche avec debounce
  useEffect(() => {
    if (!searchInput.trim()) {
      resetBpi();
      setSearched(false);
      setSelectedBpiResult(null);
      return;
    }

    const timer = setTimeout(() => {
      if (searchMode === 'bpi_code') {
        lookupBpi(searchInput);
      } else {
        lookupByCrewName(searchInput);
      }
      setSearched(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, searchMode, lookupBpi, lookupByCrewName, resetBpi]);

  // Pré-remplir les champs quand un BPI est trouvé (recherche par code)
  useEffect(() => {
    if (bpiResult?.found && searchMode === 'bpi_code') {
      // Pré-remplir le nom de l'équipage si disponible
      if (bpiResult.crewName) {
        setValue('name', bpiResult.crewName);
      }
      // Pré-remplir le produit si disponible
      if (bpiResult.productId) {
        setValue('product_id', bpiResult.productId);
      }
    }
  }, [bpiResult, searchMode, setValue]);

  // Pré-remplir quand on sélectionne un résultat multiple
  useEffect(() => {
    if (selectedBpiResult) {
      if (selectedBpiResult.crewName) {
        setValue('name', selectedBpiResult.crewName);
      }
      if (selectedBpiResult.productId) {
        setValue('product_id', selectedBpiResult.productId);
      }
    }
  }, [selectedBpiResult, setValue]);

  // Réinitialiser à la fermeture du modal
  useEffect(() => {
    if (!open) {
      setSearchInput('');
      setSearched(false);
      setSelectedBpiResult(null);
      setSearchMode('bpi_code');
      resetBpi();
    }
  }, [open, resetBpi]);

  // Handler pour le changement d'input
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setSelectedBpiResult(null);
  }, []);

  // Handler pour changer le mode de recherche
  const handleModeChange = useCallback(
    (mode: SearchMode) => {
      setSearchMode(mode);
      setSearchInput('');
      setSearched(false);
      setSelectedBpiResult(null);
      resetBpi();
    },
    [resetBpi]
  );

  // Handler pour sélectionner un résultat multiple
  const handleSelectBpiResult = useCallback((result: BpiLookupResult) => {
    setSelectedBpiResult(result);
    // Mettre à jour l'input avec le code BPI sélectionné
    if (result.bpiCode) {
      setSearchInput(result.bpiCode);
    }
  }, []);

  // Obtenir les contacts effectifs (du résultat sélectionné ou du résultat unique)
  const getEffectiveContacts = useCallback((): BpiContact[] => {
    if (selectedBpiResult) {
      return selectedBpiResult.contacts;
    }
    if (bpiResult?.found) {
      return bpiResult.contacts;
    }
    return [];
  }, [selectedBpiResult, bpiResult]);

  // Obtenir le code BPI effectif
  const getEffectiveBpiCode = useCallback((): string => {
    if (selectedBpiResult?.bpiCode) {
      return selectedBpiResult.bpiCode;
    }
    if (bpiResult?.bpiCode) {
      return bpiResult.bpiCode;
    }
    return searchInput.trim();
  }, [selectedBpiResult, bpiResult, searchInput]);

  async function handleFormSubmit(data: CreateCrewInput) {
    // Inclure le bpi_code dans les données et passer les contacts trouvés
    const crewData: CreateCrewInput = {
      ...data,
      bpi_code: getEffectiveBpiCode() || undefined,
    };

    await onSubmit({
      crewData,
      bpiContacts: getEffectiveContacts(),
    });
    reset();
    setSearchInput('');
    setSearched(false);
    setSelectedBpiResult(null);
    resetBpi();
    onOpenChange(false);
  }

  // Déterminer si on affiche les résultats multiples
  const showMultipleResults =
    searchMode === 'crew_name' &&
    searched &&
    !bpiLoading &&
    multipleResults?.found &&
    !selectedBpiResult;

  // Déterminer si on affiche le résultat unique
  const showSingleResult =
    (searchMode === 'bpi_code' && searched && !bpiLoading && searchInput.trim()) ||
    selectedBpiResult;

  // Le résultat à afficher (sélectionné ou trouvé)
  const displayResult = selectedBpiResult || bpiResult;

  return (
    <Modal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title="Créer un équipage"
      size="xl"
      footer={
        <ModalFooter
          onCancel={() => onOpenChange(false)}
          cancelLabel="Annuler"
          submitLabel="Créer"
          isSubmitting={isSubmitting}
          formId="create-crew-form"
        />
      }
    >
      <FormProvider {...methods}>
        <form
          id="create-crew-form"
          onSubmit={methods.handleSubmit(handleFormSubmit)}
          className="space-y-6 px-1"
        >
          {/* Recherche BPI / Nom d'équipage */}
          <div className="space-y-3">
            {/* Toggle mode de recherche */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={searchMode === 'bpi_code' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleModeChange('bpi_code')}
                className="flex items-center gap-1.5"
              >
                <Hash className="h-3.5 w-3.5" />
                Par code BPI
              </Button>
              <Button
                type="button"
                variant={searchMode === 'crew_name' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleModeChange('crew_name')}
                className="flex items-center gap-1.5"
              >
                <Search className="h-3.5 w-3.5" />
                Par nom d'équipage
              </Button>
            </div>

            {/* Champ de recherche */}
            <div className="relative">
              <label
                htmlFor="search_input"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {searchMode === 'bpi_code' ? 'Code BPI (optionnel)' : "Nom d'équipage (optionnel)"}
              </label>
              <div className="relative mt-1.5">
                <input
                  id="search_input"
                  type="text"
                  value={searchInput}
                  onChange={handleSearchChange}
                  placeholder={
                    searchMode === 'bpi_code' ? 'BPI-XXXXXX' : 'Ex: Les Cigales du Désert'
                  }
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
                {bpiLoading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {searchMode === 'bpi_code'
                  ? 'Renseignez un code BPI pour associer automatiquement les contacts et pré-remplir les champs'
                  : "Recherchez par nom d'équipage si vous n'avez pas le code BPI"}
              </p>
            </div>

            {/* Résultats multiples (recherche par nom) */}
            {showMultipleResults && multipleResults && (
              <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-3">
                <div className="flex items-center gap-2 text-blue-700 mb-2">
                  <Search className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {multipleResults.results.length} BPI trouvé
                    {multipleResults.results.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {multipleResults.results.map((result) => (
                    <button
                      key={result.bpiCode}
                      type="button"
                      onClick={() => handleSelectBpiResult(result)}
                      className="w-full text-left p-2 rounded border border-blue-100 bg-white hover:bg-blue-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-blue-800">{result.bpiCode}</span>
                        {result.productName && (
                          <span className="text-xs text-blue-600">{result.productName}</span>
                        )}
                      </div>
                      {result.crewName && (
                        <p className="text-sm text-gray-700 mt-0.5">{result.crewName}</p>
                      )}
                      <div className="flex gap-1 mt-1">
                        {result.contacts.map((contact) => (
                          <Badge key={contact.id} variant="outline" className="text-xs">
                            {contact.first_name} {contact.last_name}
                          </Badge>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Aucun résultat (recherche par nom) */}
            {searchMode === 'crew_name' &&
              searched &&
              !bpiLoading &&
              searchInput.trim() &&
              !multipleResults?.found &&
              !selectedBpiResult && (
                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="text-sm font-medium">Aucun BPI trouvé pour ce nom</span>
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    Essayez avec un autre terme ou passez à la recherche par code BPI.
                  </p>
                </div>
              )}

            {/* Résultat unique (recherche par code BPI ou sélection) */}
            {showSingleResult && displayResult && (
              <div className="mt-2">
                {displayResult.found ? (
                  <div className="rounded-md border border-green-200 bg-green-50 p-3">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        {displayResult.contacts.length} contact
                        {displayResult.contacts.length > 1 ? 's' : ''} trouvé
                        {displayResult.contacts.length > 1 ? 's' : ''}
                        {displayResult.bpiCode && ` - ${displayResult.bpiCode}`}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {displayResult.contacts.map((contact) => (
                        <Badge key={contact.id} variant="outline" className="bg-white">
                          <Users className="h-3 w-3 mr-1" />
                          {contact.first_name} {contact.last_name}
                        </Badge>
                      ))}
                    </div>
                    {(displayResult.crewName || displayResult.productName) && (
                      <div className="mt-2 text-xs text-green-600 space-y-0.5">
                        {displayResult.crewName && (
                          <p>
                            <span className="font-medium">Équipage :</span> {displayResult.crewName}{' '}
                            <span className="text-green-500">(pré-rempli)</span>
                          </p>
                        )}
                        {displayResult.productName && (
                          <p>
                            <span className="font-medium">Produit :</span>{' '}
                            {displayResult.productName}{' '}
                            <span className="text-green-500">(pré-rempli)</span>
                          </p>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-green-600 mt-2">
                      Ces contacts seront automatiquement ajoutés à l'équipage
                    </p>
                  </div>
                ) : (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                    <div className="flex items-center gap-2 text-amber-700">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        Aucun prospect trouvé pour ce code BPI
                      </span>
                    </div>
                    <p className="text-xs text-amber-600 mt-1">
                      L'équipage sera créé sans membres associés. Vous pourrez les ajouter
                      manuellement.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Informations de base */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                control={control}
                label="Nom d'équipage"
                name="name"
                placeholder="Les Cigales du Désert"
              />

              <FormInput
                control={control}
                label="Numéro d'équipage"
                name="crew_number"
                placeholder="EQ116"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormSelect
                control={control}
                label="Statut"
                name="status"
                options={STATUS_SELECT_OPTIONS}
              />

              <FormSelect
                control={control}
                label="Probabilité"
                name="probability"
                required
                options={PROBABILITY_SELECT_OPTIONS}
              />
            </div>

            <FormSelect
              control={control}
              label="Produit"
              name="product_id"
              options={[
                { value: '', label: 'Aucun' },
                ...products.map((product) => ({
                  value: product.id,
                  label: product.name,
                })),
              ]}
            />
          </div>

          {/* Relations */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormSelect
                control={control}
                label="Véhicule"
                name="vehicle_id"
                options={[
                  { value: '', label: 'Aucun' },
                  ...vehicles.map((vehicle) => ({
                    value: vehicle.id,
                    label: vehicle.registration,
                  })),
                ]}
              />

              <FormSelect
                control={control}
                label="Association"
                name="association_id"
                options={[
                  { value: '', label: 'Aucune' },
                  ...associations.map((association) => ({
                    value: association.id,
                    label: association.name,
                  })),
                ]}
              />
            </div>

            <FormSelect
              control={control}
              label="Réduction"
              name="discount_id"
              options={[
                { value: '', label: 'Aucune' },
                ...discounts.map((discount) => ({
                  value: discount.id,
                  label: discount.name,
                })),
              ]}
            />
          </div>

          {/* Financement */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormSelect
                control={control}
                label="Type de financement"
                name="financing_type"
                options={Object.values(CREW_FINANCING_TYPE).map((type) => ({
                  value: type,
                  label: CREW_FINANCING_TYPE_LABELS[type],
                }))}
              />

              <FormInput
                control={control}
                label="Budget (€)"
                name="budget"
                type="number"
                placeholder="8500"
                valueAsNumber
              />
            </div>
          </div>

          {/* Checkboxes */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <Controller
                control={control}
                name="has_derogation"
                render={({ field }) => (
                  <Checkbox
                    checked={field.value || false}
                    onChange={field.onChange}
                    label="Dérogation"
                  />
                )}
              />

              <Controller
                control={control}
                name="organizes_pvd"
                render={({ field }) => (
                  <Checkbox
                    checked={field.value || false}
                    onChange={field.onChange}
                    label="Organisation PVD"
                  />
                )}
              />
            </div>
          </div>

          {/* Motif Annulation ou Report */}
          <FormInput
            control={control}
            label="Motif Annulation ou Report"
            name="report"
            placeholder="Aucun"
          />

          {/* Notes */}
          <FormInput
            control={control}
            label="Notes"
            name="notes"
            placeholder="Notes sur l'équipage..."
          />
        </form>
      </FormProvider>
    </Modal>
  );
}
